import { FormControl } from '@@/form-components/FormControl';
import { Switch } from '@@/form-components/SwitchField/Switch';
import { InputList } from '@@/form-components/InputList';
import { InputGroup } from '@@/form-components/InputGroup';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { FormSection } from '@@/form-components/FormSection';
import { TextTip } from '@@/Tip/TextTip';

import { KubernetesSecurityFormValues } from './types';
import { procMountTypeOptions, runAsUserTypeOptions } from './constants';
import { VolumeTypeItem } from './VolumeTypeItem';
import { HostPathItem } from './HostPathItem';
import { FlexDriverItem } from './FlexDriverItem';
import { RangeItem } from './RangeItem';
import { CapabilityItem } from './CapabilityItem';
import { SELinuxOptionItem } from './SELinuxOptionItem';
import { ProfileItem } from './ProfileItem';
import { SysctlItem } from './SysctlItem';

interface ConstraintsSectionProps {
  formData: KubernetesSecurityFormValues;
  onChange: <K extends keyof KubernetesSecurityFormValues>(
    key: K,
    value: KubernetesSecurityFormValues[K]
  ) => void;
}

export function ConstraintsSection({
  formData,
  onChange,
}: ConstraintsSectionProps) {
  return (
    <FormSection title="Constraints">
      {/* Restrict running privileged containers */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls whether any container in a pod can enable privileged mode
        </p>
        <FormControl
          inputId="restrict-privileged-containers"
          label="Restrict running privileged containers"
          size="large"
        >
          <Switch
            id="restrict-privileged-containers"
            data-cy="restrict-privileged-containers-toggle"
            name="restrictPrivilegedContainers"
            className="space-right"
            checked={formData.restrictPrivilegedContainers || false}
            onChange={(value) =>
              onChange('restrictPrivilegedContainers', value)
            }
          />
        </FormControl>
      </div>

      {/* Restrict host namespace */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls whether the pod containers can share the process ID namespace
          and host IPC namespace
        </p>
        <FormControl
          inputId="restrict-host-namespace"
          label="Restrict host namespace"
          size="large"
        >
          <Switch
            id="restrict-host-namespace"
            data-cy="restrict-host-namespace-toggle"
            name="restrictHostNamespace"
            className="space-right"
            checked={formData.restrictHostNamespace || false}
            onChange={(value) => onChange('restrictHostNamespace', value)}
          />
        </FormControl>
      </div>

      {/* Restrict host networking ports */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls whether the pod may use networking ports
        </p>
        <FormControl
          inputId="restrict-host-networking-ports"
          label="Restrict host networking ports"
          size="large"
        >
          <Switch
            id="restrict-host-networking-ports"
            data-cy="restrict-host-networking-ports-toggle"
            name="restrictHostNetworkingPorts"
            className="space-right"
            checked={formData.restrictHostNetworkingPorts || false}
            onChange={(value) => onChange('restrictHostNetworkingPorts', value)}
          />
        </FormControl>

        {/* Expandable: Host networking ports details */}
        {formData.restrictHostNetworkingPorts && (
          <>
            <FormControl
              inputId="k8s-constraint-host-network-toggle"
              label={<span className="!pl-4">Host network</span>}
              size="large"
            >
              <Switch
                id="k8s-constraint-host-network-toggle"
                data-cy="k8s-constraint-host-network-toggle"
                name="hostNetwork"
                className="space-right"
                checked={formData.hostNetwork || false}
                onChange={(value) => onChange('hostNetwork', value)}
              />
            </FormControl>
            <div className="form-group col-sm-12 !mb-0 p-0">
              <div className="col-sm-12">
                <label
                  className="control-label text-left !pl-4"
                  htmlFor="k8s-constraint-host-port-range"
                >
                  Host port range
                </label>
              </div>
              <div className="form-group">
                <div className="col-sm-12">
                  <div className="flex gap-2 !pl-4">
                    <InputGroup size="small" className="w-24">
                      <InputGroup.Addon>Min</InputGroup.Addon>
                      <InputGroup.Input
                        type="number"
                        value={formData.hostPortMin || 0}
                        onChange={(e) =>
                          onChange('hostPortMin', parseInt(e.target.value, 10))
                        }
                        data-cy="k8s-constraint-host-port-min"
                        id="k8s-constraint-host-port-range"
                      />
                    </InputGroup>
                    <InputGroup size="small" className="w-24">
                      <InputGroup.Addon>Max</InputGroup.Addon>
                      <InputGroup.Input
                        type="number"
                        value={formData.hostPortMax || 0}
                        onChange={(e) =>
                          onChange('hostPortMax', parseInt(e.target.value, 10))
                        }
                        data-cy="k8s-constraint-host-port-max"
                      />
                    </InputGroup>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls which volume sources may be used
        </p>
        <FormControl
          inputId="restrict-volume-types"
          label="Restrict volume types"
          tooltip="Controls which volume sources may be used"
          size="large"
        >
          <Switch
            id="restrict-volume-types"
            data-cy="restrict-volume-types-toggle"
            name="restrictVolumeTypes"
            className="space-right"
            checked={formData.restrictVolumeTypes || false}
            onChange={(value) => onChange('restrictVolumeTypes', value)}
          />
        </FormControl>

        {/* Expandable: Volume types */}
        {formData.restrictVolumeTypes && (
          <>
            <div className="text-muted small col-sm-12 !mb-0 p-0 !pl-4">
              Examples of volume types are configMap, downwardAPI, emptyDir,
              persistentVolumeClaim, secret, projected, hostPath, flexVolume
            </div>
            <div className="col-sm-12 !pl-4">
              <InputList<string>
                value={formData.allowedVolumeTypes || []}
                label="Allowed volume types"
                onChange={(types) => onChange('allowedVolumeTypes', types)}
                renderItem={(item, onChange, index) => (
                  <VolumeTypeItem
                    item={item}
                    onChange={onChange}
                    index={index}
                  />
                )}
                itemBuilder={() => ''}
                addLabel="Add type"
                data-cy="allowed-volume-types"
              />
            </div>
          </>
        )}
      </div>

      {/* Restrict host filesystem paths */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls the host paths that are allowed when using hostPath volumes
        </p>
        <FormControl
          inputId="restrict-host-filesystem-paths"
          label="Restrict host filesystem paths"
          size="large"
        >
          <Switch
            id="restrict-host-filesystem-paths"
            data-cy="restrict-host-filesystem-paths-toggle"
            name="restrictHostFilesystemPaths"
            className="space-right"
            checked={formData.restrictHostFilesystemPaths || false}
            onChange={(value) => onChange('restrictHostFilesystemPaths', value)}
          />
        </FormControl>

        {/* Expandable: Host filesystem paths */}
        {formData.restrictHostFilesystemPaths && (
          <div className="col-sm-12 !pl-4">
            <InputList<{ pathPrefix: string; readonly: boolean }>
              value={formData.allowedHostPaths || []}
              onChange={(paths) => onChange('allowedHostPaths', paths)}
              renderItem={(item, onChange, index) => (
                <HostPathItem item={item} onChange={onChange} index={index} />
              )}
              itemBuilder={() => ({ pathPrefix: '', readonly: false })}
              label="Allowed host paths"
              addLabel="Add path"
              data-cy="allowed-host-paths"
            />
          </div>
        )}
      </div>

      {/* Restrict drivers */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls the FlexVolume drivers that are allowed
        </p>
        <FormControl
          inputId="restrict-drivers"
          label="Restrict drivers"
          size="large"
        >
          <Switch
            id="restrict-drivers"
            data-cy="restrict-drivers-toggle"
            name="restrictDrivers"
            className="space-right"
            checked={formData.restrictDrivers || false}
            onChange={(value) => onChange('restrictDrivers', value)}
          />
        </FormControl>

        {/* Expandable: FlexVolume drivers */}
        {formData.restrictDrivers && (
          <div className="col-sm-12 !pl-4">
            <InputList<string>
              value={formData.allowedFlexVolumeDrivers || []}
              onChange={(drivers) =>
                onChange('allowedFlexVolumeDrivers', drivers)
              }
              renderItem={(item, onChange, index) => (
                <FlexDriverItem item={item} onChange={onChange} index={index} />
              )}
              itemBuilder={() => ''}
              addLabel="Add driver"
              data-cy="allowed-flex-drivers"
            />
          </div>
        )}
      </div>

      {/* Require read-only root filesystem */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls that containers must run with a read-only root filesystem
        </p>
        <FormControl
          inputId="require-read-only-root-filesystem"
          label="Require read-only root filesystem"
          size="large"
        >
          <Switch
            id="require-read-only-root-filesystem"
            data-cy="require-read-only-root-filesystem-toggle"
            name="requireReadOnlyRootFilesystem"
            className="space-right"
            checked={formData.requireReadOnlyRootFilesystem || false}
            onChange={(value) =>
              onChange('requireReadOnlyRootFilesystem', value)
            }
          />
        </FormControl>
      </div>

      {/* Restrict User and group ids */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls which user ID or group ID the containers are run with or
          which group IDs get added
        </p>
        <FormControl
          inputId="restrict-user-and-group-ids"
          label="Restrict user and group ids"
          size="large"
        >
          <Switch
            id="restrict-user-and-group-ids"
            data-cy="restrict-user-and-group-ids-toggle"
            name="restrictUserAndGroupIds"
            className="space-right"
            checked={formData.restrictUserAndGroupIds || false}
            onChange={(value) => onChange('restrictUserAndGroupIds', value)}
          />
        </FormControl>

        {/* Expandable: User and group IDs */}
        {formData.restrictUserAndGroupIds && (
          <div className="form-group-messages row px-2">
            {/* Run as User */}
            <div className="form-group col-sm-12 !mb-0 p-0 !pl-4">
              <div className="col-sm-12 !pl-0">
                <label
                  className="control-label text-left"
                  htmlFor="k8s-constraint-run-as-user"
                >
                  Run as User
                </label>
              </div>
              <div className="form-inline">
                <PortainerSelect
                  options={runAsUserTypeOptions}
                  value={formData.runAsUserType || 'RunAsAny'}
                  onChange={(value) => onChange('runAsUserType', value || '')}
                  data-cy="k8s-constraint-run-as-user-type"
                  isClearable={false}
                  size="sm"
                  inputId="k8s-constraint-run-as-user-type"
                />
                {formData.runAsUserType === 'MustRunAs' && (
                  <div className="col-sm-12">
                    <InputList<{ min: number; max: number }>
                      value={formData.runAsUserRanges || []}
                      onChange={(ranges) => onChange('runAsUserRanges', ranges)}
                      renderItem={(item, onChange, index) => (
                        <RangeItem
                          item={item}
                          onChange={onChange}
                          index={index}
                          label="run-as-user-range"
                        />
                      )}
                      itemBuilder={() => ({ min: 0, max: 0 })}
                      addLabel="Add range"
                      data-cy="run-as-user-ranges"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Run as Group */}
            <div className="form-group col-sm-12 !mb-0 p-0 top-space-sm !pl-4">
              <div className="col-sm-12 !pl-0">
                <label
                  className="control-label text-left"
                  htmlFor="k8s-constraint-run-as-group-type"
                >
                  Run as Group
                </label>
              </div>
              <div className="form-inline">
                <PortainerSelect
                  options={runAsUserTypeOptions}
                  value={formData.runAsGroupType || 'RunAsAny'}
                  onChange={(value) => onChange('runAsGroupType', value || '')}
                  data-cy="k8s-constraint-run-as-group-type"
                  isClearable={false}
                  size="sm"
                  inputId="k8s-constraint-run-as-group-type"
                />
                {formData.runAsGroupType === 'MustRunAs' && (
                  <div className="col-sm-12">
                    <InputList<{ min: number; max: number }>
                      value={formData.runAsGroupRanges || []}
                      onChange={(ranges) =>
                        onChange('runAsGroupRanges', ranges)
                      }
                      renderItem={(item, onChange, index) => (
                        <RangeItem
                          item={item}
                          onChange={onChange}
                          index={index}
                          label="run-as-group-range"
                        />
                      )}
                      itemBuilder={() => ({ min: 0, max: 0 })}
                      addLabel="Add range"
                      data-cy="run-as-group-ranges"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Supplemental groups */}
            <div className="form-group col-sm-12 !mb-0 p-0 top-space-sm !pl-4">
              <div className="col-sm-12 !pl-0">
                <label
                  className="control-label text-left"
                  htmlFor="k8s-constraint-supplemental-groups-type"
                >
                  Supplemental groups
                </label>
              </div>
              <div className="form-inline">
                <PortainerSelect
                  options={runAsUserTypeOptions}
                  value={formData.supplementalGroupsType || 'RunAsAny'}
                  onChange={(value) =>
                    onChange('supplementalGroupsType', value || '')
                  }
                  data-cy="k8s-constraint-supplemental-groups-type"
                  isClearable={false}
                  size="sm"
                  inputId="k8s-constraint-supplemental-groups-type"
                />
                {formData.supplementalGroupsType === 'MustRunAs' && (
                  <div className="col-sm-12">
                    <InputList<{ min: number; max: number }>
                      value={formData.supplementalGroupsRanges || []}
                      onChange={(ranges) =>
                        onChange('supplementalGroupsRanges', ranges)
                      }
                      renderItem={(item, onChange, index) => (
                        <RangeItem
                          item={item}
                          onChange={onChange}
                          index={index}
                          label="supplemental-groups-range"
                        />
                      )}
                      itemBuilder={() => ({ min: 0, max: 0 })}
                      addLabel="Add range"
                      data-cy="supplemental-groups-ranges"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* FS group */}
            <div className="form-group col-sm-12 !mb-0 p-0 top-space-sm !pl-4">
              <div className="col-sm-12 !pl-0">
                <label
                  className="control-label text-left"
                  htmlFor="k8s-constraint-fs-group-type"
                >
                  FS group
                </label>
              </div>
              <div className="form-inline">
                <PortainerSelect
                  options={runAsUserTypeOptions}
                  value={formData.fsGroupType || 'RunAsAny'}
                  onChange={(value) => onChange('fsGroupType', value || '')}
                  data-cy="k8s-constraint-fs-group-type"
                  isClearable={false}
                  size="sm"
                  inputId="k8s-constraint-fs-group-type"
                />
                {formData.fsGroupType === 'MustRunAs' && (
                  <div className="col-sm-12">
                    <InputList<{ min: number; max: number }>
                      value={formData.fsGroupRanges || []}
                      onChange={(ranges) => onChange('fsGroupRanges', ranges)}
                      renderItem={(item, onChange, index) => (
                        <RangeItem
                          item={item}
                          onChange={onChange}
                          index={index}
                          label="fs-group-range"
                        />
                      )}
                      itemBuilder={() => ({ min: 0, max: 0 })}
                      addLabel="Add range"
                      data-cy="fs-group-ranges"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Restrict escalation to root privileges */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls the user privileges and prevents files from enabling extra
          capabilities
        </p>
        <FormControl
          inputId="restrict-escalation-to-root-privileges"
          label="Restrict escalation to root privileges"
          size="large"
        >
          <Switch
            id="restrict-escalation-to-root-privileges"
            data-cy="restrict-escalation-to-root-privileges-toggle"
            name="restrictEscalationToRootPrivileges"
            className="space-right"
            checked={formData.restrictEscalationToRootPrivileges || false}
            onChange={(value) =>
              onChange('restrictEscalationToRootPrivileges', value)
            }
          />
        </FormControl>
      </div>

      {/* Restrict Linux capabilities */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls the permission checks on Linux processes
        </p>
        <FormControl
          inputId="restrict-linux-capabilities"
          label="Restrict Linux capabilities"
          size="large"
        >
          <Switch
            id="restrict-linux-capabilities"
            data-cy="restrict-linux-capabilities-toggle"
            name="restrictLinuxCapabilities"
            className="space-right"
            checked={formData.restrictLinuxCapabilities || false}
            onChange={(value) => onChange('restrictLinuxCapabilities', value)}
          />
        </FormControl>

        {/* Expandable: Linux capabilities */}
        {formData.restrictLinuxCapabilities && (
          <>
            {/* Allowed capabilities */}
            <div className="col-sm-12 !pl-4">
              <InputList<string>
                label="Allowed capabilities"
                value={formData.allowedCapabilities || []}
                onChange={(caps) => onChange('allowedCapabilities', caps)}
                renderItem={(item, onChange, index) => (
                  <CapabilityItem
                    item={item}
                    onChange={onChange}
                    index={index}
                  />
                )}
                itemBuilder={() => ''}
                addLabel="Add capability"
                data-cy="allowed-capabilities"
              />
            </div>

            {/* Required drop capabilities */}
            <div className="col-sm-12 !pl-4">
              <InputList<string>
                label="Required drop capabilities"
                value={formData.requiredDropCapabilities || []}
                onChange={(caps) => onChange('requiredDropCapabilities', caps)}
                renderItem={(item, onChange, index) => (
                  <CapabilityItem
                    item={item}
                    onChange={onChange}
                    index={index}
                  />
                )}
                itemBuilder={() => ''}
                addLabel="Add capability"
                data-cy="required-drop-capabilities"
              />
            </div>

            {/* Default add capabilities */}
            <div className="col-sm-12 !pl-4">
              <InputList<string>
                label="Default add capabilities"
                value={formData.defaultAddCapabilities || []}
                onChange={(caps) => onChange('defaultAddCapabilities', caps)}
                renderItem={(item, onChange, index) => (
                  <CapabilityItem
                    item={item}
                    onChange={onChange}
                    index={index}
                  />
                )}
                itemBuilder={() => ''}
                addLabel="Add capability"
                data-cy="default-add-capabilities"
              />
            </div>
          </>
        )}
      </div>

      {/* Restrict SELinux security context */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls the SELinux context of the container
        </p>
        <FormControl
          inputId="restrict-selinux-security-context"
          label="Restrict SELinux security context"
          size="large"
        >
          <Switch
            id="restrict-selinux-security-context"
            data-cy="restrict-selinux-security-context-toggle"
            name="restrictSELinuxSecurityContext"
            className="space-right"
            checked={formData.restrictSeLinuxSecurityContext || false}
            onChange={(value) =>
              onChange('restrictSeLinuxSecurityContext', value)
            }
          />
        </FormControl>

        {/* Expandable: SELinux security context */}
        {formData.restrictSeLinuxSecurityContext && (
          <div className="col-sm-12 !pl-4">
            <InputList<{
              level: string;
              role: string;
              type: string;
              user: string;
            }>
              label="Allowed SELinux options"
              value={formData.seLinuxOptions || []}
              onChange={(options) => onChange('seLinuxOptions', options)}
              renderItem={(item, onChange, index) => (
                <SELinuxOptionItem
                  item={item}
                  onChange={onChange}
                  index={index}
                />
              )}
              itemBuilder={() => ({
                level: '',
                role: '',
                type: '',
                user: '',
              })}
              addLabel="Add option"
              data-cy="selinux-options"
            />
          </div>
        )}
      </div>

      {/* Restrict Proc Mount types */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls the type of proc mount to use for containers
        </p>
        <FormControl
          inputId="restrict-proc-mount-types"
          label="Restrict Proc Mount types"
          size="large"
        >
          <Switch
            id="restrict-proc-mount-types"
            data-cy="restrict-proc-mount-types-toggle"
            name="restrictProcMountTypes"
            className="space-right"
            checked={formData.restrictProcMountTypes || false}
            onChange={(value) => onChange('restrictProcMountTypes', value)}
          />
        </FormControl>

        {/* Expandable: Proc mount types */}
        {formData.restrictProcMountTypes && (
          <div className="form-group-messages row px-2">
            <div className="form-group col-sm-12 !mb-0 p-0 !pl-4">
              <TextTip color="orange">
                Unmasked is only applicable if the ProcMountType{' '}
                <a
                  href="https://kubernetes.io/docs/reference/command-line-tools-reference/feature-gates/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  feature gate
                </a>{' '}
                has been used to enable control over the type of proc mounts for
                containers.
              </TextTip>
            </div>

            <FormControl
              inputId="k8s-constraint-proc-mount-type"
              label={<span className="!pl-4">Proc mount type</span>}
              size="large"
            >
              <PortainerSelect
                inputId="k8s-constraint-proc-mount-type"
                data-cy="k8s-constraint-proc-mount-type"
                value={formData.procMountType || 'Default'}
                onChange={(value) =>
                  onChange('procMountType', value || 'Default')
                }
                options={procMountTypeOptions}
                isClearable={false}
                size="sm"
              />
            </FormControl>
          </div>
        )}
      </div>

      {/* Restrict AppArmor profiles */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls the AppArmor profile used by containers
        </p>
        <FormControl
          inputId="restrict-apparmor-profiles"
          label="Restrict AppArmor profiles"
          size="large"
        >
          <Switch
            id="restrict-apparmor-profiles"
            data-cy="restrict-apparmor-profiles-toggle"
            name="restrictAppArmorProfiles"
            className="space-right"
            checked={formData.restrictAppArmorProfiles || false}
            onChange={(value) => onChange('restrictAppArmorProfiles', value)}
          />
        </FormControl>
        {/* Expandable: AppArmor profiles */}
        {formData.restrictAppArmorProfiles && (
          <div className="col-sm-12 !pl-4">
            <InputList<string>
              label="Allowed AppArmor profiles"
              value={formData.allowedAppArmorProfiles || []}
              onChange={(profiles) =>
                onChange('allowedAppArmorProfiles', profiles)
              }
              renderItem={(item, onChange, index) => (
                <ProfileItem item={item} onChange={onChange} index={index} />
              )}
              itemBuilder={() => ''}
              addLabel="Add profile"
              data-cy="allowed-apparmor-profiles"
            />
          </div>
        )}
      </div>

      {/* Restrict seccomp profiles */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls the seccomp profile used by containers or pods
        </p>
        <FormControl
          inputId="restrict-seccomp-profiles"
          label="Restrict seccomp profiles"
          size="large"
        >
          <Switch
            id="restrict-seccomp-profiles"
            data-cy="restrict-seccomp-profiles-toggle"
            name="restrictSeccompProfiles"
            className="space-right"
            checked={formData.restrictSeccompProfiles || false}
            onChange={(value) => onChange('restrictSeccompProfiles', value)}
          />
        </FormControl>

        {/* Expandable: Seccomp profiles */}
        {formData.restrictSeccompProfiles && (
          <div className="col-sm-12 !pl-4">
            <InputList<string>
              label="Allowed Seccomp profiles"
              value={formData.allowedSeccompProfiles || []}
              onChange={(profiles) =>
                onChange('allowedSeccompProfiles', profiles)
              }
              renderItem={(item, onChange, index) => (
                <ProfileItem
                  item={item}
                  onChange={onChange}
                  index={index}
                  placeholder="Localhost/profile.json or RuntimeDefault or Unconfined"
                />
              )}
              itemBuilder={() => ''}
              addLabel="Add profile"
              data-cy="allowed-seccomp-profiles"
            />
          </div>
        )}
      </div>

      {/* Restrict sysctl profiles */}
      <div className="mb-10 row">
        <p className="text-muted text-xs mb-2">
          Controls the sysctl profile used by containers
        </p>
        <FormControl
          inputId="restrict-sysctl-profiles"
          label="Restrict sysctl profiles"
          size="large"
        >
          <Switch
            id="restrict-sysctl-profiles"
            data-cy="restrict-sysctl-profiles-toggle"
            name="restrictSysctlProfiles"
            className="space-right"
            checked={formData.restrictSysctlProfiles || false}
            onChange={(value) => onChange('restrictSysctlProfiles', value)}
          />
        </FormControl>

        {/* Expandable: Sysctls */}
        {formData.restrictSysctlProfiles && (
          <div className="col-sm-12 !pl-4">
            <InputList<string>
              label="Forbidden sysctls"
              value={formData.forbiddenSysctls || []}
              onChange={(sysctls) => onChange('forbiddenSysctls', sysctls)}
              renderItem={(item, onChange, index) => (
                <SysctlItem item={item} onChange={onChange} index={index} />
              )}
              itemBuilder={() => ''}
              addLabel="Add sysctl"
              data-cy="forbidden-sysctls"
            />
          </div>
        )}
      </div>
    </FormSection>
  );
}
